import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI, curatedAPI, externalJobsAPI, providersAPI } from '../lib/api';
import { extUrl } from '../lib/url';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  BarChart3, Users, Shield, Flag, FileText, Search,
  Ban, CheckCircle, XCircle, AlertTriangle, Eye, Trash2,
  TrendingUp, MessageCircle, Briefcase, Calendar, ShoppingBag, Home,
  UserCog, Save, Activity, UserPlus, ExternalLink, Settings, ShieldAlert,
  ToggleLeft, ToggleRight, SlidersHorizontal, GraduationCap, MapPin,
  Clock, Globe, Layers, Monitor, Rss, Download, Video, Radio, Loader2, Camera, Mail, Send, Award, Building, Gift
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import usePageTitle from '../hooks/usePageTitle';
import AdminAnalyticsDashboard from '../components/AdminAnalyticsDashboard';
import TrustSafetyTab from '../components/admin/TrustSafetyTab';
import RadioStationsTab from '../components/admin/RadioStationsTab';
import SocialQueueTab from '../components/admin/SocialQueueTab';
import NewsletterTab from '../components/admin/NewsletterTab';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area, Legend } from 'recharts';

const AdminPanel = () => {
  usePageTitle('Admin');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [providers, setProviders] = useState([]);
  const [reports, setReports] = useState([]);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusDialog, setStatusDialog] = useState(null);
  const [statusAction, setStatusAction] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [reportFilter, setReportFilter] = useState('pending');
  const [rolesDialog, setRolesDialog] = useState(null);
  const [editingRoles, setEditingRoles] = useState([]);
  const [userDetailDialog, setUserDetailDialog] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [providerDialog, setProviderDialog] = useState(null);
  const [providerCredentialsEdit, setProviderCredentialsEdit] = useState('');
  const [savingProvider, setSavingProvider] = useState(false);
  const [uploadingProviderPhoto, setUploadingProviderPhoto] = useState(false);
  const [invalidProviders, setInvalidProviders] = useState([]);
  const [loadingInvalidProviders, setLoadingInvalidProviders] = useState(false);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteNote, setInviteNote] = useState('');
  const [inviteCanBypass, setInviteCanBypass] = useState(true);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [fpReferrals, setFpReferrals] = useState([]);
  const [fpForm, setFpForm] = useState({
    prospect_name: '',
    prospect_email: '',
    prospect_business: '',
    prospect_link: '',
    prospect_category: '',
    referrer_name: '',
    referrer_institution: '',
    referrer_relationship: '',
    note: '',
  });
  const [creatingFpReferral, setCreatingFpReferral] = useState(false);
  const [partnerInvites, setPartnerInvites] = useState([]);
  const [piForm, setPiForm] = useState({
    email: '',
    role: '',
    plan: '',
    organization: '',
    contact_name: '',
    comp_months: 6,
    note: '',
  });
  const [creatingPartnerInvite, setCreatingPartnerInvite] = useState(false);
  const [roleCounts, setRoleCounts] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [moderationSettings, setModerationSettings] = useState(null);
  const [moderationQueue, setModerationQueue] = useState([]);
  const [moderationQueueTotal, setModerationQueueTotal] = useState(0);
  const [modQueueFilter, setModQueueFilter] = useState('pending');
  const [savingModSettings, setSavingModSettings] = useState(false);
  const [trafficPeriod, setTrafficPeriod] = useState('daily');

  // TikTok curation state
  const [tiktokUrl, setTiktokUrl] = useState('');
  const [tiktokBulkUrls, setTiktokBulkUrls] = useState('');
  const [themes, setThemes] = useState([]);
  const [creators, setCreators] = useState([]);
  const [newThemeName, setNewThemeName] = useState('');
  const [newThemeDesc, setNewThemeDesc] = useState('');
  const [newCreatorUser, setNewCreatorUser] = useState('');
  const [newCreatorUrl, setNewCreatorUrl] = useState('');

  const ALL_ROLES = ['admin', 'moderator', 'alumni_association', 'recruiter', 'sponsor', 'advertiser'];

  useEffect(() => {
    if (user?.role !== 'admin' && !user?.roles?.includes('admin')) {
      navigate('/dashboard');
      return;
    }
    fetchStats();
    fetchRoleCounts();
    curatedAPI.getThemes().then(r => setThemes(r.data || [])).catch(() => {});
    curatedAPI.getCreators().then(r => setCreators(r.data || [])).catch(() => {});
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      const res = await adminAPI.getStats();
      setStats(res.data);
    } catch (e) {
      toast.error('Failed to load admin stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (search = '') => {
    try {
      const res = await adminAPI.listUsers({ search, limit: 50 });
      setUsers(res.data.users);
      setUsersTotal(res.data.total);
    } catch (e) {
      toast.error('Failed to load users');
    }
  };

  const fetchProviders = async () => {
    try {
      const res = await adminAPI.listProviders({ limit: 50 });
      setProviders(res.data.providers);
    } catch (e) {
      toast.error('Failed to load providers');
    }
  };

  const fetchReports = async (status = 'pending') => {
    try {
      const res = await adminAPI.listReports({ status, limit: 50 });
      setReports(res.data.reports);
      setReportsTotal(res.data.total);
    } catch (e) {
      toast.error('Failed to load reports');
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const res = await adminAPI.getAuditLogs({ limit: 100 });
      setAuditLogs(res.data.logs);
    } catch (e) {
      toast.error('Failed to load audit logs');
    }
  };

  const fetchRoleCounts = async () => {
    try {
      const res = await adminAPI.getRoleCounts();
      setRoleCounts(res.data.role_counts || {});
    } catch (e) {
      console.error('Failed to load role counts');
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await adminAPI.getAnalytics();
      setAnalytics(res.data);
    } catch (e) {
      toast.error('Failed to load analytics');
    }
  };

  const fetchModerationSettings = async () => {
    try {
      const res = await adminAPI.getModerationSettings();
      setModerationSettings(res.data);
    } catch (e) {
      toast.error('Failed to load moderation settings');
    }
  };

  const fetchModerationQueue = async (status = 'pending') => {
    try {
      const res = await adminAPI.getModerationQueue({ status, limit: 50 });
      setModerationQueue(res.data.items || []);
      setModerationQueueTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Failed to load moderation queue');
    }
  };

  const handleSaveModerationSettings = async () => {
    if (!moderationSettings) return;
    setSavingModSettings(true);
    try {
      const res = await adminAPI.updateModerationSettings({
        ai_moderation_enabled: moderationSettings.ai_moderation_enabled,
        strictness: moderationSettings.strictness,
        categories: moderationSettings.categories
      });
      setModerationSettings(res.data);
      toast.success('Moderation settings saved');
    } catch (e) {
      toast.error('Failed to save moderation settings');
    } finally {
      setSavingModSettings(false);
    }
  };

  const handleModerationAction = async (flagId, action) => {
    try {
      await adminAPI.resolveModerationItem(flagId, { action });
      toast.success(`Content ${action}d`);
      fetchModerationQueue(modQueueFilter);
    } catch (e) {
      toast.error('Failed to process moderation action');
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'users' || tab === 'roles') fetchUsers();
    if (tab === 'providers') { fetchProviders(); fetchInvalidProviders(); }
    if (tab === 'reports') fetchReports(reportFilter);
    if (tab === 'audit') fetchAuditLogs();
    if (tab === 'analytics') fetchAnalytics();
    if (tab === 'moderation') { fetchModerationSettings(); fetchModerationQueue(modQueueFilter); }
    if (tab === 'overview') { fetchStats(); fetchRoleCounts(); }
    if (tab === 'invites') fetchInvites();
    if (tab === 'fp-referrals') fetchFpReferrals();
    if (tab === 'partner-invites') fetchPartnerInvites();
  };

  const fetchFpReferrals = async () => {
    try {
      const res = await adminAPI.listFpReferrals();
      setFpReferrals(res.data.referrals || []);
    } catch (e) {
      toast.error('Failed to load FP referrals');
    }
  };

  const handleCreateFpReferral = async (e) => {
    e?.preventDefault();
    if (!fpForm.prospect_name.trim() || !fpForm.prospect_email.trim() || !fpForm.prospect_email.includes('@')) {
      toast.error('Prospect name and a valid email are required');
      return;
    }
    if (!fpForm.referrer_name.trim()) {
      toast.error('Referrer name is required');
      return;
    }
    setCreatingFpReferral(true);
    try {
      await adminAPI.createFpReferral(fpForm);
      toast.success(`Founding Provider referral sent to ${fpForm.prospect_email.trim()}`);
      setFpForm({
        prospect_name: '',
        prospect_email: '',
        prospect_business: '',
        prospect_link: '',
        prospect_category: '',
        referrer_name: '',
        referrer_institution: '',
        referrer_relationship: '',
        note: '',
      });
      fetchFpReferrals();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send referral');
    } finally {
      setCreatingFpReferral(false);
    }
  };

  const handleResendFpReferral = async (referralId) => {
    try {
      await adminAPI.resendFpReferral(referralId);
      toast.success('Referral email resent');
      fetchFpReferrals();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend referral');
    }
  };

  const handleDeleteFpReferral = async (referralId) => {
    try {
      await adminAPI.deleteFpReferral(referralId);
      toast.success('Referral revoked');
      fetchFpReferrals();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke referral');
    }
  };

  const PARTNER_INVITE_PLANS = {
    recruiter: [
      { id: 'rec_starter', name: 'Recruiter Starter — $149/mo' },
      { id: 'rec_pro', name: 'Recruiter Pro — $299/mo' },
      { id: 'rec_enterprise', name: 'Recruiter Enterprise — $499/mo' },
    ],
    sponsor: [
      { id: 'sp_starter', name: 'Sponsor Starter — $199/mo' },
      { id: 'sp_growth', name: 'Sponsor Growth — $499/mo' },
      { id: 'sp_enterprise', name: 'Sponsor Enterprise — $999/mo' },
    ],
    alumni_association: [
      { id: 'aa_basic', name: 'Alumni Association Basic — $99/mo' },
      { id: 'aa_pro', name: 'Alumni Association Pro — $199/mo' },
      { id: 'aa_enterprise', name: 'Alumni Association Enterprise — $299/mo' },
    ],
  };

  const fetchPartnerInvites = async () => {
    try {
      const res = await adminAPI.listPartnerInvites();
      setPartnerInvites(res.data.invites || []);
    } catch (e) {
      toast.error('Failed to load partner invites');
    }
  };

  const handleCreatePartnerInvite = async (e) => {
    e?.preventDefault();
    if (!piForm.email.trim() || !piForm.email.includes('@')) {
      toast.error('A valid email is required');
      return;
    }
    if (!piForm.role) {
      toast.error('Please select a role');
      return;
    }
    setCreatingPartnerInvite(true);
    try {
      await adminAPI.createPartnerInvite({
        ...piForm,
        comp_months: Number(piForm.comp_months) || 6,
        plan: piForm.plan || undefined,
      });
      toast.success(`Invite sent to ${piForm.email.trim()}`);
      setPiForm({ email: '', role: '', plan: '', organization: '', contact_name: '', comp_months: 6, note: '' });
      fetchPartnerInvites();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send invite');
    } finally {
      setCreatingPartnerInvite(false);
    }
  };

  const handleResendPartnerInvite = async (inviteId) => {
    try {
      await adminAPI.resendPartnerInvite(inviteId);
      toast.success('Invite email resent');
      fetchPartnerInvites();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend invite');
    }
  };

  const handleDeletePartnerInvite = async (inviteId) => {
    try {
      await adminAPI.deletePartnerInvite(inviteId);
      toast.success('Invite revoked');
      fetchPartnerInvites();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke invite');
    }
  };

  const fetchInvites = async () => {
    try {
      const res = await adminAPI.listInvites();
      setInvites(res.data.invites || []);
    } catch (e) {
      toast.error('Failed to load invites');
    }
  };

  const handleCreateInvite = async (e) => {
    e?.preventDefault();
    if (!inviteEmail.trim() || !inviteEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setCreatingInvite(true);
    try {
      await adminAPI.createInvite({
        email: inviteEmail.trim(),
        can_bypass_hbcu: inviteCanBypass,
        note: inviteNote.trim() || undefined,
      });
      toast.success(`Invite sent to ${inviteEmail.trim()}`);
      setInviteEmail('');
      setInviteNote('');
      setInviteCanBypass(true);
      fetchInvites();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send invite');
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    try {
      await adminAPI.deleteInvite(inviteId);
      toast.success('Invite revoked');
      fetchInvites();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke invite');
    }
  };

  const openRolesDialog = (u) => {
    setRolesDialog(u);
    setEditingRoles(u.roles || []);
  };

  const toggleRole = (role) => {
    setEditingRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  };

  const handleUpdateRoles = async () => {
    if (!rolesDialog) return;
    try {
      await adminAPI.updateUserRoles(rolesDialog.user_id, editingRoles);
      toast.success('Roles updated');
      setRolesDialog(null);
      fetchUsers(searchQuery);
    } catch (e) {
      toast.error('Failed to update roles');
    }
  };

  const handleUserStatusChange = async () => {
    if (!statusDialog || !statusAction) return;
    try {
      await adminAPI.updateUserStatus(statusDialog.user_id, {
        status: statusAction,
        reason: statusReason
      });
      toast.success(`User ${statusAction}`);
      setStatusDialog(null);
      setStatusAction('');
      setStatusReason('');
      fetchUsers(searchQuery);
    } catch (e) {
      toast.error('Failed to update user status');
    }
  };

  const handleVerifyProvider = async (providerId, verified) => {
    try {
      await adminAPI.verifyProvider(providerId, { verified });
      toast.success(`Provider ${verified ? 'verified' : 'unverified'}`);
      fetchProviders();
      if (providerDialog?.provider_id === providerId) {
        setProviderDialog(p => p ? { ...p, verified } : p);
      }
    } catch (e) {
      toast.error('Failed to update provider');
    }
  };

  const openProviderDetail = (provider) => {
    setProviderDialog(provider);
    setProviderCredentialsEdit((provider.credentials || []).join(', '));
  };

  const handleProviderPhotoUpload = async (file) => {
    if (!file || !providerDialog) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file.'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB.'); return; }
    setUploadingProviderPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/founding-provider-headshot`, {
        method: 'POST',
        body: formData,
      });
      const uploadData = await uploadRes.json();
      if (!uploadRes.ok) throw new Error(uploadData.detail || 'Upload failed');
      const updateRes = await providersAPI.update(providerDialog.provider_id, { profile_photo: uploadData.url });
      toast.success('Photo updated');
      setProviderDialog(updateRes.data);
      fetchProviders();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Failed to upload photo');
    } finally {
      setUploadingProviderPhoto(false);
    }
  };

  const handleSaveProviderCredentials = async () => {
    if (!providerDialog) return;
    setSavingProvider(true);
    try {
      const credentials = providerCredentialsEdit
        ? providerCredentialsEdit.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      const res = await providersAPI.update(providerDialog.provider_id, { credentials });
      toast.success('Credentials updated');
      setProviderDialog(res.data);
      setProviderCredentialsEdit((res.data.credentials || []).join(', '));
      fetchProviders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update credentials');
    } finally {
      setSavingProvider(false);
    }
  };

  const fetchInvalidProviders = async () => {
    setLoadingInvalidProviders(true);
    try {
      const res = await adminAPI.listInvalidProviders();
      setInvalidProviders(res.data.invalid || []);
    } catch (e) {
      // Endpoint may not be deployed yet; ignore silently
      setInvalidProviders([]);
    } finally {
      setLoadingInvalidProviders(false);
    }
  };

  const handleDeleteInvalidProvider = async (providerId) => {
    try {
      await adminAPI.deleteProvider(providerId);
      toast.success('Provider record deleted');
      fetchInvalidProviders();
      fetchProviders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete provider');
    }
  };

  const handleResolveReport = async (reportId, action) => {
    try {
      await adminAPI.resolveReport(reportId, { action });
      toast.success('Report resolved');
      fetchReports(reportFilter);
    } catch (e) {
      toast.error('Failed to resolve report');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(searchQuery);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await adminAPI.deleteUser(userId);
      toast.success('User deleted successfully');
      setDeleteConfirm(null);
      setUserDetailDialog(null);
      fetchUsers(searchQuery);
    } catch (e) {
      toast.error('Failed to delete user');
    }
  };

  const getProfileCompletion = (u) => {
    const fields = ['name', 'email', 'institution', 'graduation_year', 'major', 'title', 'company', 'location', 'profile_photo', 'bio'];
    const filled = fields.filter(f => u[f] && String(u[f]).trim() !== '').length;
    return Math.round((filled / fields.length) * 100);
  };

  const statusBadge = (status) => {
    const map = {
      active: { variant: 'outline', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      suspended: { variant: 'outline', className: 'bg-amber-50 text-amber-700 border-amber-200' },
      banned: { variant: 'outline', className: 'bg-red-50 text-red-700 border-red-200' }
    };
    const style = map[status] || map.active;
    return <Badge variant={style.variant} className={style.className}>{status || 'active'}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dark-slate"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-panel">
        <div className="mb-8">
          <h1 className="font-heading font-extrabold text-3xl text-dark-slate mb-1">Admin Panel</h1>
          <p className="text-slate-gray">Manage users, content, and platform settings</p>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="flex flex-wrap w-full max-w-4xl mb-8 bg-slate-100 rounded-xl p-1 gap-1">
            <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-overview">
              <BarChart3 className="w-4 h-4 mr-2" />Overview
            </TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-analytics">
              <Activity className="w-4 h-4 mr-2" />Analytics
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-users">
              <Users className="w-4 h-4 mr-2" />Users
            </TabsTrigger>
            <TabsTrigger value="roles" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-roles">
              <UserCog className="w-4 h-4 mr-2" />Roles
            </TabsTrigger>
            <TabsTrigger value="moderation" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-moderation">
              <ShieldAlert className="w-4 h-4 mr-2" />Moderation
            </TabsTrigger>
            <TabsTrigger value="trust-safety" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-trust-safety">
              <Shield className="w-4 h-4 mr-2" />Trust &amp; Safety
            </TabsTrigger>
            <TabsTrigger value="providers" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-providers">
              <Shield className="w-4 h-4 mr-2" />Providers
            </TabsTrigger>
            <TabsTrigger value="invites" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-invites">
              <Mail className="w-4 h-4 mr-2" />Invites
            </TabsTrigger>
            <TabsTrigger value="fp-referrals" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-fp-referrals">
              <Award className="w-4 h-4 mr-2" />FP Referrals
            </TabsTrigger>
            <TabsTrigger value="partner-invites" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-partner-invites">
              <Gift className="w-4 h-4 mr-2" />Partner Invites
            </TabsTrigger>
            <TabsTrigger value="reports" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-reports">
              <Flag className="w-4 h-4 mr-2" />Reports
            </TabsTrigger>
            <TabsTrigger value="audit" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-audit">
              <FileText className="w-4 h-4 mr-2" />Audit
            </TabsTrigger>
            <TabsTrigger value="content" className="rounded-lg data-[state=active]:bg-white">
              <Rss className="w-4 h-4 mr-2" />Content
            </TabsTrigger>
            <TabsTrigger value="radio-stations" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-radio-stations">
              <Radio className="w-4 h-4 mr-2" />Radio Stations
            </TabsTrigger>
            <TabsTrigger value="social-queue" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-social-queue">
              <Send className="w-4 h-4 mr-2" />Social Queue
            </TabsTrigger>
            <TabsTrigger value="newsletter" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-tab-newsletter">
              <Mail className="w-4 h-4 mr-2" />Newsletter
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview">
            {stats && (
              <div className="space-y-6">
                {/* Role Counts Dashboard */}
                <div>
                  <h3 className="font-heading font-bold text-lg text-dark-slate mb-3">Users by Role</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {ALL_ROLES.map(role => {
                      const roleColors = {
                        admin: 'red', moderator: 'orange', alumni_association: 'blue',
                        recruiter: 'purple', sponsor: 'emerald', advertiser: 'amber'
                      };
                      const color = roleColors[role] || 'slate';
                      return (
                        <div key={role} className="bg-white rounded-2xl p-4 border border-slate-100 text-center">
                          <p className={`text-2xl font-heading font-bold text-${color}-600`}>{roleCounts[role] || 0}</p>
                          <p className="text-xs text-slate-gray capitalize mt-1">{role.replace(/_/g, ' ')}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <StatCard icon={Users} label="Total Users" value={stats.users?.total || 0} sub={`+${stats.users?.this_week || 0} this week`} color="blue" onClick={() => handleTabChange('users')} />
                  <StatCard icon={Shield} label="Providers" value={stats.providers?.total || 0} sub={`${stats.providers?.pending || 0} pending`} color="emerald" onClick={() => handleTabChange('providers')} />
                  <StatCard icon={Flag} label="Pending Reports" value={stats.reports?.pending || 0} sub={`${stats.reports?.resolved || 0} resolved`} color="amber" onClick={() => handleTabChange('reports')} />
                  <StatCard icon={MessageCircle} label="Messages Today" value={stats.engagement?.messages_today || 0} sub={`${stats.engagement?.active_chats || 0} active chats`} color="purple" />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                  <ContentCard icon={TrendingUp} label="Posts" value={stats.content?.posts || 0} />
                  <ContentCard icon={Briefcase} label="Jobs" value={stats.content?.jobs || 0} />
                  <ContentCard icon={Calendar} label="Events" value={stats.content?.events || 0} />
                  <ContentCard icon={Home} label="Housing" value={stats.content?.housing || 0} />
                  <ContentCard icon={ShoppingBag} label="Marketplace" value={stats.content?.marketplace || 0} />
                </div>

                {/* Preview Role Panels */}
                <div className="bg-white rounded-2xl p-6 border border-slate-100">
                  <h3 className="font-heading font-bold text-lg text-dark-slate mb-3">Preview Role Panels</h3>
                  <p className="text-sm text-slate-gray mb-4">View role-specific panels as an admin to verify their functionality.</p>
                  <div className="flex flex-wrap gap-3">
                    <Button variant="outline" className="rounded-xl" onClick={() => navigate('/alumni-association')}>
                      <ExternalLink className="w-4 h-4 mr-2" /> Alumni Association Panel
                    </Button>
                    <Button variant="outline" className="rounded-xl" onClick={() => navigate('/recruiter')}>
                      <ExternalLink className="w-4 h-4 mr-2" /> Recruiter Panel
                    </Button>
                    <Button variant="outline" className="rounded-xl" onClick={() => navigate('/sponsor')}>
                      <ExternalLink className="w-4 h-4 mr-2" /> Sponsor Panel
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white rounded-2xl p-6 border border-slate-100">
                    <h3 className="font-heading font-bold text-lg text-dark-slate mb-4">Content Overview</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={[
                        { name: 'Posts', count: stats.content?.posts || 0 },
                        { name: 'Jobs', count: stats.content?.jobs || 0 },
                        { name: 'Events', count: stats.content?.events || 0 },
                        { name: 'Housing', count: stats.content?.housing || 0 },
                        { name: 'Market', count: stats.content?.marketplace || 0 },
                      ]}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#1E293B" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="bg-white rounded-2xl p-6 border border-slate-100">
                    <h3 className="font-heading font-bold text-lg text-dark-slate mb-4">Provider Status</h3>
                    <div className="flex items-center justify-center">
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Verified', value: stats.providers?.verified || 1 },
                              { name: 'Pending', value: stats.providers?.pending || 0 },
                            ]}
                            cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                            paddingAngle={5} dataKey="value"
                          >
                            <Cell fill="#10b981" />
                            <Cell fill="#f59e0b" />
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex justify-center gap-6 mt-2 text-sm">
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-emerald-500" />
                        <span className="text-slate-gray">Verified ({stats.providers?.verified || 0})</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-amber-500" />
                        <span className="text-slate-gray">Pending ({stats.providers?.pending || 0})</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ANALYTICS TAB */}
          <TabsContent value="analytics">
            <div className="space-y-8">
              {/* Tableau-style Traffic Dashboard */}
              <AdminAnalyticsDashboard />

              {/* Divider */}
              <div className="border-t pt-6" style={{ borderColor: 'hsl(var(--border))' }}>
                <h2 className="font-heading font-bold text-xl text-dark-slate mb-1">Platform Metrics</h2>
                <p className="text-sm text-slate-gray mb-6">User demographics, engagement, and content breakdown</p>
              </div>

              {analytics ? (
                <>
                  {/* Top-level KPIs */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <StatCard icon={Users} label="Total Users" value={analytics.total_users} color="blue" />
                    <StatCard icon={Activity} label="DAU (Today)" value={analytics.dau} sub="Daily Active Users" color="emerald" />
                    <StatCard icon={TrendingUp} label="MAU (30d)" value={analytics.mau} sub="Monthly Active Users" color="purple" />
                    <StatCard icon={UserPlus} label="New This Week" value={analytics.signups_this_week} sub="Signups in last 7 days" color="amber" />
                  </div>

                  {/* DAU/MAU Ratio */}
                  <div className="bg-white rounded-2xl p-6 border border-slate-100">
                    <h3 className="font-heading font-bold text-lg text-dark-slate mb-2">Engagement Ratio</h3>
                    <div className="flex items-center gap-6">
                      <div>
                        <p className="text-3xl font-heading font-bold text-dark-slate">
                          {analytics.mau > 0 ? Math.round((analytics.dau / analytics.mau) * 100) : 0}%
                        </p>
                        <p className="text-sm text-slate-gray">DAU/MAU Ratio</p>
                      </div>
                      <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 rounded-full transition-all"
                          style={{ width: `${analytics.mau > 0 ? Math.min((analytics.dau / analytics.mau) * 100, 100) : 0}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Traffic Over Time — Daily / Weekly / Monthly toggle */}
                  <div className="bg-white rounded-2xl p-6 border border-slate-100">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading font-bold text-lg text-dark-slate">Active Users Over Time</h3>
                      <div className="flex gap-2">
                        {[
                          { key: 'daily', label: 'Daily' },
                          { key: 'weekly', label: 'Weekly' },
                          { key: 'monthly', label: 'Monthly' }
                        ].map(p => (
                          <Button
                            key={p.key}
                            variant={trafficPeriod === p.key ? 'default' : 'outline'}
                            size="sm"
                            className="rounded-lg"
                            onClick={() => setTrafficPeriod(p.key)}
                          >
                            {p.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                    {(() => {
                      const dataMap = {
                        daily: analytics.daily_active_users,
                        weekly: analytics.weekly_active_users,
                        monthly: analytics.monthly_active_users
                      };
                      const chartData = dataMap[trafficPeriod] || [];
                      return chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={280}>
                          <AreaChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                            <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                            <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }} />
                            <Area type="monotone" dataKey="count" stroke="#6366f1" fill="#6366f1" fillOpacity={0.15} strokeWidth={2} name="Active Users" />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-sm text-slate-gray text-center py-8">No traffic data available yet</p>
                      );
                    })()}
                  </div>

                  {/* Daily Signups Chart */}
                  <div className="bg-white rounded-2xl p-6 border border-slate-100">
                    <h3 className="font-heading font-bold text-lg text-dark-slate mb-4">Daily Signups (Last 30 Days)</h3>
                    {analytics.daily_signups && analytics.daily_signups.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={analytics.daily_signups}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" interval={4} />
                          <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" allowDecimals={false} />
                          <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }} />
                          <Bar dataKey="count" fill="#1e293b" radius={[4, 4, 0, 0]} name="Signups" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-sm text-slate-gray">No signup data available</p>
                    )}
                  </div>

                  {/* Institution Metrics */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Top Institutions by Signups */}
                    <div className="bg-white rounded-2xl p-6 border border-slate-100">
                      <div className="flex items-center gap-2 mb-4">
                        <GraduationCap className="w-5 h-5 text-blue-600" />
                        <h3 className="font-heading font-bold text-lg text-dark-slate">Top Institutions (Signups)</h3>
                      </div>
                      {analytics.top_institutions_by_signups?.length > 0 ? (
                        <div className="space-y-3">
                          {analytics.top_institutions_by_signups.map((inst, i) => (
                            <div key={i} className="flex items-center justify-between">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <span className="text-xs font-bold text-slate-gray w-5 text-center">{i + 1}</span>
                                <span className="text-sm text-dark-slate truncate">{inst.institution || 'Not specified'}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-blue-500 rounded-full"
                                    style={{ width: `${analytics.top_institutions_by_signups[0]?.count > 0 ? (inst.count / analytics.top_institutions_by_signups[0].count) * 100 : 0}%` }}
                                  />
                                </div>
                                <span className="text-sm font-semibold text-dark-slate w-10 text-right">{inst.count}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-gray text-center py-6">No institution data</p>
                      )}
                    </div>

                    {/* Top Institutions by Active Users */}
                    <div className="bg-white rounded-2xl p-6 border border-slate-100">
                      <div className="flex items-center gap-2 mb-4">
                        <Activity className="w-5 h-5 text-emerald-600" />
                        <h3 className="font-heading font-bold text-lg text-dark-slate">Most Active Institutions (7d)</h3>
                      </div>
                      {analytics.top_institutions_by_active?.length > 0 ? (
                        <div className="space-y-3">
                          {analytics.top_institutions_by_active.map((inst, i) => (
                            <div key={i} className="flex items-center justify-between">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <span className="text-xs font-bold text-slate-gray w-5 text-center">{i + 1}</span>
                                <span className="text-sm text-dark-slate truncate">{inst.institution || 'Not specified'}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-emerald-500 rounded-full"
                                    style={{ width: `${analytics.top_institutions_by_active[0]?.count > 0 ? (inst.count / analytics.top_institutions_by_active[0].count) * 100 : 0}%` }}
                                  />
                                </div>
                                <span className="text-sm font-semibold text-dark-slate w-10 text-right">{inst.count}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-gray text-center py-6">No active institution data</p>
                      )}
                    </div>
                  </div>

                  {/* Content Metrics */}
                  <div className="bg-white rounded-2xl p-6 border border-slate-100">
                    <div className="flex items-center gap-2 mb-4">
                      <Layers className="w-5 h-5 text-purple-600" />
                      <h3 className="font-heading font-bold text-lg text-dark-slate">Content Overview</h3>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
                      {[
                        { label: 'Feed Posts', value: analytics.content_totals?.feed_posts || 0, icon: MessageCircle, color: 'blue' },
                        { label: 'Jobs', value: analytics.content_totals?.jobs || 0, icon: Briefcase, color: 'purple' },
                        { label: 'Events', value: analytics.content_totals?.events || 0, icon: Calendar, color: 'amber' },
                        { label: 'Housing', value: analytics.content_totals?.housing || 0, icon: Home, color: 'emerald' },
                        { label: 'Marketplace', value: analytics.content_totals?.marketplace || 0, icon: ShoppingBag, color: 'red' }
                      ].map(item => (
                        <div key={item.label} className="text-center p-3 rounded-xl bg-slate-50 border border-slate-100">
                          <item.icon className={`w-5 h-5 mx-auto mb-2 text-${item.color}-500`} />
                          <p className="text-xl font-heading font-bold text-dark-slate">{item.value}</p>
                          <p className="text-xs text-slate-gray">{item.label}</p>
                        </div>
                      ))}
                    </div>
                    {analytics.posts_per_day?.length > 0 && (
                      <>
                        <h4 className="font-medium text-dark-slate text-sm mb-3">Feed Posts Per Day (30d)</h4>
                        <ResponsiveContainer width="100%" height={180}>
                          <BarChart data={analytics.posts_per_day}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8" interval={4} />
                            <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
                            <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }} />
                            <Bar dataKey="count" fill="#8b5cf6" radius={[3, 3, 0, 0]} name="Posts" />
                          </BarChart>
                        </ResponsiveContainer>
                      </>
                    )}
                  </div>

                  {/* Page Views */}
                  {analytics.top_pages?.length > 0 && (
                    <div className="bg-white rounded-2xl p-6 border border-slate-100">
                      <div className="flex items-center gap-2 mb-4">
                        <Monitor className="w-5 h-5 text-indigo-600" />
                        <h3 className="font-heading font-bold text-lg text-dark-slate">Most Visited Pages (30d)</h3>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          {analytics.top_pages.map((pg, i) => (
                            <div key={i} className="flex items-center justify-between py-1.5">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="text-xs font-bold text-slate-gray w-5">{i + 1}</span>
                                <code className="text-sm text-dark-slate truncate bg-slate-50 px-2 py-0.5 rounded">{pg.page}</code>
                              </div>
                              <span className="text-sm font-semibold text-dark-slate ml-3">{pg.views?.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                        {analytics.page_views_per_day?.length > 0 && (
                          <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={analytics.page_views_per_day}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" interval={4} />
                              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
                              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }} />
                              <Line type="monotone" dataKey="views" stroke="#6366f1" strokeWidth={2} dot={false} name="Page Views" />
                            </LineChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Top Active Users */}
                  <div className="bg-white rounded-2xl p-6 border border-slate-100">
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingUp className="w-5 h-5 text-amber-600" />
                      <h3 className="font-heading font-bold text-lg text-dark-slate">Most Active Users</h3>
                    </div>
                    {analytics.top_active_users?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-slate-100 bg-slate-50/50">
                              <th className="text-left p-3 text-xs font-semibold text-slate-gray">#</th>
                              <th className="text-left p-3 text-xs font-semibold text-slate-gray">User</th>
                              <th className="text-left p-3 text-xs font-semibold text-slate-gray">Institution</th>
                              <th className="text-left p-3 text-xs font-semibold text-slate-gray">Posts</th>
                              <th className="text-left p-3 text-xs font-semibold text-slate-gray">Last Active</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.top_active_users.map((u, i) => (
                              <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                                <td className="p-3 text-sm text-slate-gray font-bold">{i + 1}</td>
                                <td className="p-3">
                                  <p className="text-sm font-medium text-dark-slate">{u.name || 'Unknown'}</p>
                                  <p className="text-xs text-slate-gray">{u.email}</p>
                                </td>
                                <td className="p-3 text-sm text-slate-gray">{u.institution || '-'}</td>
                                <td className="p-3">
                                  <span className="text-sm font-semibold text-dark-slate">{u.post_count}</span>
                                </td>
                                <td className="p-3 text-xs text-slate-gray">
                                  {u.last_active ? formatDistanceToNow(new Date(u.last_active), { addSuffix: true }) : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-gray text-center py-6">No user activity data</p>
                    )}
                  </div>

                  {/* Demographics */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Graduation Year Distribution */}
                    <div className="bg-white rounded-2xl p-6 border border-slate-100">
                      <div className="flex items-center gap-2 mb-4">
                        <GraduationCap className="w-5 h-5 text-blue-600" />
                        <h3 className="font-heading font-bold text-lg text-dark-slate">Graduation Year</h3>
                      </div>
                      {analytics.graduation_year_distribution?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={analytics.graduation_year_distribution.slice(-20)}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="year" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                            <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
                            <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }} />
                            <Bar dataKey="count" fill="#3b82f6" radius={[3, 3, 0, 0]} name="Alumni" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-sm text-slate-gray text-center py-6">No graduation data</p>
                      )}
                    </div>

                    {/* Industry Distribution */}
                    <div className="bg-white rounded-2xl p-6 border border-slate-100">
                      <div className="flex items-center gap-2 mb-4">
                        <Briefcase className="w-5 h-5 text-purple-600" />
                        <h3 className="font-heading font-bold text-lg text-dark-slate">Industry Breakdown</h3>
                      </div>
                      {analytics.industry_distribution?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie
                              data={analytics.industry_distribution.slice(0, 8)}
                              dataKey="count"
                              nameKey="industry"
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              label={({ industry, percent }) => `${(industry || '').substring(0, 12)} ${(percent * 100).toFixed(0)}%`}
                            >
                              {analytics.industry_distribution.slice(0, 8).map((_, idx) => (
                                <Cell key={idx} fill={['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#14b8a6'][idx % 8]} />
                              ))}
                            </Pie>
                            <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-sm text-slate-gray text-center py-6">No industry data</p>
                      )}
                    </div>

                    {/* Location Distribution */}
                    <div className="bg-white rounded-2xl p-6 border border-slate-100">
                      <div className="flex items-center gap-2 mb-4">
                        <MapPin className="w-5 h-5 text-red-500" />
                        <h3 className="font-heading font-bold text-lg text-dark-slate">Top Locations</h3>
                      </div>
                      {analytics.location_distribution?.length > 0 ? (
                        <div className="space-y-2">
                          {analytics.location_distribution.map((loc, i) => (
                            <div key={i} className="flex items-center justify-between py-1">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="text-xs font-bold text-slate-gray w-5">{i + 1}</span>
                                <span className="text-sm text-dark-slate truncate">{loc.location || 'Not specified'}</span>
                              </div>
                              <span className="text-sm font-semibold text-dark-slate">{loc.count}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-gray text-center py-6">No location data</p>
                      )}
                    </div>

                    {/* Greek Life Distribution */}
                    <div className="bg-white rounded-2xl p-6 border border-slate-100">
                      <div className="flex items-center gap-2 mb-4">
                        <Globe className="w-5 h-5 text-emerald-600" />
                        <h3 className="font-heading font-bold text-lg text-dark-slate">Greek Life</h3>
                      </div>
                      {analytics.greek_life_distribution?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie
                              data={analytics.greek_life_distribution.slice(0, 10)}
                              dataKey="count"
                              nameKey="org"
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              label={({ org, percent }) => `${(org || 'None').substring(0, 10)} ${(percent * 100).toFixed(0)}%`}
                            >
                              {analytics.greek_life_distribution.slice(0, 10).map((_, idx) => (
                                <Cell key={idx} fill={['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#6366f1', '#f97316', '#84cc16'][idx % 10]} />
                              ))}
                            </Pie>
                            <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-sm text-slate-gray text-center py-6">No Greek life data</p>
                      )}
                    </div>
                  </div>

                  {/* Profile Completion Distribution */}
                  <div className="bg-white rounded-2xl p-6 border border-slate-100">
                    <div className="flex items-center gap-2 mb-4">
                      <Clock className="w-5 h-5 text-indigo-600" />
                      <h3 className="font-heading font-bold text-lg text-dark-slate">Profile Completion</h3>
                    </div>
                    <div className="flex items-center gap-6 mb-4">
                      <div>
                        <p className="text-3xl font-heading font-bold text-dark-slate">
                          {analytics.avg_profile_completion != null ? Math.round(analytics.avg_profile_completion) : 0}%
                        </p>
                        <p className="text-sm text-slate-gray">Average Completion</p>
                      </div>
                    </div>
                    {analytics.profile_completion_ranges && (
                      <div className="grid grid-cols-4 gap-4">
                        {[
                          { key: '0_25', label: '0-25%', color: 'red' },
                          { key: '26_50', label: '26-50%', color: 'amber' },
                          { key: '51_75', label: '51-75%', color: 'blue' },
                          { key: '76_100', label: '76-100%', color: 'emerald' }
                        ].map(range => (
                          <div key={range.key} className={`text-center p-4 rounded-xl bg-${range.color}-50 border border-${range.color}-100`}>
                            <p className={`text-2xl font-heading font-bold text-${range.color}-600`}>
                              {analytics.profile_completion_ranges[range.key] || 0}
                            </p>
                            <p className="text-xs text-slate-gray mt-1">{range.label}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dark-slate"></div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* MODERATION TAB */}
          <TabsContent value="moderation">
            <div className="space-y-6">
              {/* Moderation Settings */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="font-heading font-bold text-lg text-dark-slate">Content Moderation Settings</h3>
                    <p className="text-sm text-slate-gray">Configure AI-powered content moderation for the platform</p>
                  </div>
                  <Button
                    onClick={handleSaveModerationSettings}
                    disabled={savingModSettings || !moderationSettings}
                    className="bg-dark-slate text-white rounded-xl"
                  >
                    <Save className="w-4 h-4 mr-1" /> {savingModSettings ? 'Saving...' : 'Save Settings'}
                  </Button>
                </div>

                {moderationSettings ? (
                  <div className="space-y-6">
                    {/* AI Moderation Toggle */}
                    <div className="flex items-center justify-between p-4 rounded-xl border border-slate-100 bg-slate-50/50">
                      <div className="flex items-center gap-3">
                        {moderationSettings.ai_moderation_enabled ? (
                          <ToggleRight className="w-6 h-6 text-emerald-600" />
                        ) : (
                          <ToggleLeft className="w-6 h-6 text-slate-400" />
                        )}
                        <div>
                          <p className="font-medium text-dark-slate">AI Content Moderation</p>
                          <p className="text-xs text-slate-gray">Automatically screen new posts using Claude AI</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setModerationSettings(prev => ({
                          ...prev,
                          ai_moderation_enabled: !prev.ai_moderation_enabled
                        }))}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          moderationSettings.ai_moderation_enabled ? 'bg-emerald-500' : 'bg-slate-300'
                        }`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          moderationSettings.ai_moderation_enabled ? 'translate-x-6' : ''
                        }`} />
                      </button>
                    </div>

                    {/* Strictness Level */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <SlidersHorizontal className="w-4 h-4 text-slate-gray" />
                        <p className="font-medium text-dark-slate">Strictness Level</p>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { value: 'low', label: 'Low', desc: 'Block explicit content only' },
                          { value: 'medium', label: 'Medium', desc: 'Block explicit + hate speech' },
                          { value: 'high', label: 'High', desc: 'Block explicit + hate + harassment + spam' }
                        ].map(level => (
                          <button
                            key={level.value}
                            onClick={() => setModerationSettings(prev => ({ ...prev, strictness: level.value }))}
                            className={`p-4 rounded-xl border text-left transition-all ${
                              moderationSettings.strictness === level.value
                                ? 'border-dark-slate bg-slate-50 ring-1 ring-dark-slate'
                                : 'border-slate-100 hover:border-slate-200'
                            }`}
                          >
                            <p className="font-medium text-dark-slate text-sm">{level.label}</p>
                            <p className="text-xs text-slate-gray mt-1">{level.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Category Toggles */}
                    <div className="space-y-3">
                      <p className="font-medium text-dark-slate">Moderation Categories</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {['explicit_content', 'hate_speech', 'harassment', 'spam', 'misinformation', 'self_harm'].map(cat => (
                          <label
                            key={cat}
                            className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                              moderationSettings.categories?.[cat]
                                ? 'border-dark-slate bg-slate-50'
                                : 'border-slate-100 hover:border-slate-200'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={moderationSettings.categories?.[cat] || false}
                              onChange={() => setModerationSettings(prev => ({
                                ...prev,
                                categories: {
                                  ...prev.categories,
                                  [cat]: !prev.categories?.[cat]
                                }
                              }))}
                              className="w-4 h-4 rounded border-slate-300 text-dark-slate focus:ring-dark-slate"
                            />
                            <span className="text-sm text-dark-slate capitalize">{cat.replace(/_/g, ' ')}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dark-slate"></div>
                  </div>
                )}
              </div>

              {/* Moderation Queue */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading font-bold text-lg text-dark-slate">Moderation Queue</h3>
                    <p className="text-sm text-slate-gray">{moderationQueueTotal} items need review</p>
                  </div>
                  <div className="flex gap-2">
                    {['pending', 'approved', 'rejected', 'all'].map(f => (
                      <Button
                        key={f}
                        variant={modQueueFilter === f ? 'default' : 'outline'}
                        size="sm"
                        className="rounded-lg capitalize"
                        onClick={() => { setModQueueFilter(f); fetchModerationQueue(f); }}
                      >
                        {f}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  {moderationQueue.map(item => (
                    <div key={item.flag_id} className="rounded-xl border border-slate-100 p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <ShieldAlert className="w-4 h-4 text-amber-500" />
                            <span className="font-medium text-dark-slate text-sm">
                              {item.content_type === 'post' ? 'Post' : item.content_type} flagged
                            </span>
                            <Badge variant="outline" className="text-xs capitalize bg-amber-50 text-amber-700 border-amber-200">
                              {(item.reason || 'unknown').replace(/_/g, ' ')}
                            </Badge>
                            {item.auto_action && (
                              <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600 border-slate-200">
                                Auto: {item.auto_action}
                              </Badge>
                            )}
                          </div>
                          {item.content_text && (
                            <p className="text-sm text-slate-gray mb-2 bg-slate-50 rounded-lg p-3 border border-slate-100">
                              {item.content_text}
                            </p>
                          )}
                          {item.explanation && (
                            <p className="text-xs text-slate-gray mb-2">AI Reason: {item.explanation}</p>
                          )}
                          <p className="text-xs text-slate-gray">
                            By {item.user_name || 'Unknown'} | {item.created_at ? formatDistanceToNow(new Date(item.created_at), { addSuffix: true }) : ''}
                          </p>
                        </div>
                        {item.status === 'pending' && (
                          <div className="flex gap-2 ml-4">
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-lg text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                              onClick={() => handleModerationAction(item.flag_id, 'approve')}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              className="bg-red-600 text-white rounded-lg hover:bg-red-700"
                              onClick={() => handleModerationAction(item.flag_id, 'reject')}
                            >
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </div>
                        )}
                        {item.status !== 'pending' && (
                          <Badge variant="outline" className={`text-xs ${
                            item.status === 'approved'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : 'bg-red-50 text-red-700 border-red-200'
                          }`}>
                            {item.status}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                  {moderationQueue.length === 0 && (
                    <div className="text-center py-12 text-slate-gray">
                      <ShieldAlert className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                      <p>No {modQueueFilter} items in moderation queue</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* USERS TAB */}
          <TabsContent value="users">
            <div className="space-y-4">
              <form onSubmit={handleSearch} className="flex gap-3 max-w-lg">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
                  <Input
                    placeholder="Search users by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 rounded-xl"
                    data-testid="admin-user-search"
                  />
                </div>
                <Button type="submit" className="bg-dark-slate text-white rounded-xl" data-testid="admin-user-search-btn">Search</Button>
              </form>
              <p className="text-sm text-slate-gray">{usersTotal} users found</p>

              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/50">
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">User</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Role</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Status</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Joined</th>
                        <th className="text-right p-4 text-sm font-semibold text-slate-gray">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u.user_id} className="border-b border-slate-50 hover:bg-slate-50/50">
                          <td className="p-4">
                            <div
                              className="flex items-center gap-3 cursor-pointer hover:opacity-80"
                              onClick={() => setUserDetailDialog(u)}
                              data-testid={`admin-user-detail-${u.user_id}`}
                            >
                              <Avatar className="w-9 h-9">
                                <AvatarImage src={u.profile_photo} />
                                <AvatarFallback className="bg-mint-fresh text-dark-slate text-sm">{u.name?.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-dark-slate text-sm hover:underline">{u.name}</p>
                                <p className="text-xs text-slate-gray">{u.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <Badge variant="outline" className="text-xs">{u.role || u.user_type || 'alumni'}</Badge>
                          </td>
                          <td className="p-4">{statusBadge(u.status)}</td>
                          <td className="p-4 text-sm text-slate-gray">
                            {u.created_at ? formatDistanceToNow(new Date(u.created_at), { addSuffix: true }) : '-'}
                          </td>
                          <td className="p-4 text-right">
                            {u.role !== 'admin' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setStatusDialog(u)}
                                className="text-slate-gray hover:text-dark-slate"
                                data-testid={`admin-user-action-${u.user_id}`}
                              >
                                <AlertTriangle className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ROLES TAB */}
          <TabsContent value="roles">
            <div className="space-y-4">
              <p className="text-sm text-slate-gray">Assign roles to users to grant access to specialized panels (Alumni Association, Recruiter, Sponsor).</p>
              <form onSubmit={handleSearch} className="flex gap-3 max-w-lg">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
                  <Input placeholder="Search users..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 rounded-xl" />
                </div>
                <Button type="submit" className="bg-dark-slate text-white rounded-xl">Search</Button>
              </form>

              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/50">
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">User</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Current Roles</th>
                        <th className="text-right p-4 text-sm font-semibold text-slate-gray">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u.user_id} className="border-b border-slate-50 hover:bg-slate-50/50">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <Avatar className="w-9 h-9">
                                <AvatarImage src={u.profile_photo} />
                                <AvatarFallback className="bg-mint-fresh text-dark-slate text-sm">{u.name?.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-dark-slate text-sm">{u.name}</p>
                                <p className="text-xs text-slate-gray">{u.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex flex-wrap gap-1">
                              {(u.roles || []).length > 0 ? (u.roles || []).map(r => (
                                <Badge key={r} variant="outline" className="text-xs capitalize bg-blue-50 text-blue-700 border-blue-200">{r.replace(/_/g, ' ')}</Badge>
                              )) : (
                                <span className="text-xs text-slate-gray">No special roles</span>
                              )}
                            </div>
                          </td>
                          <td className="p-4 text-right">
                            <Button size="sm" variant="outline" className="rounded-lg" onClick={() => openRolesDialog(u)}>
                              <UserCog className="w-4 h-4 mr-1" /> Edit Roles
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* TRUST & SAFETY TAB */}
          <TabsContent value="trust-safety">
            <TrustSafetyTab />
          </TabsContent>

          {/* RADIO STATIONS TAB */}
          <TabsContent value="radio-stations">
            <RadioStationsTab />
          </TabsContent>

          {/* SOCIAL QUEUE TAB */}
          <TabsContent value="social-queue">
            <SocialQueueTab />
          </TabsContent>

          {/* NEWSLETTER TAB */}
          <TabsContent value="newsletter">
            <NewsletterTab />
          </TabsContent>

          {/* PROVIDERS TAB */}
          <TabsContent value="providers">
            <div className="space-y-4">
              {/* Invalid records callout — only shown when there are any */}
              {invalidProviders.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl overflow-hidden">
                  <div className="px-5 py-4 border-b border-amber-200 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-dark-slate text-sm">{invalidProviders.length} ghost record{invalidProviders.length === 1 ? '' : 's'} hidden from listings</p>
                      <p className="text-xs text-slate-gray mt-0.5">These docs in the providers collection fail validation, so the public Business Services page silently skips them. Review and delete any you don't want around.</p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-amber-200 bg-amber-100/40">
                          <th className="text-left p-3 font-semibold text-slate-gray text-xs uppercase tracking-wide">Provider</th>
                          <th className="text-left p-3 font-semibold text-slate-gray text-xs uppercase tracking-wide">Created</th>
                          <th className="text-left p-3 font-semibold text-slate-gray text-xs uppercase tracking-wide">Validation error</th>
                          <th className="text-right p-3 font-semibold text-slate-gray text-xs uppercase tracking-wide">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invalidProviders.map(p => (
                          <tr key={p.provider_id} className="border-b border-amber-100 last:border-0">
                            <td className="p-3 align-top">
                              <p className="font-medium text-dark-slate">{p.name || <span className="text-slate-400 italic">(no name)</span>}</p>
                              <p className="font-mono text-[11px] text-slate-gray break-all">{p.provider_id || '—'}</p>
                              {p.category && <p className="text-xs text-slate-gray capitalize">{p.category.replace(/_/g, ' ')}</p>}
                              {p.verified !== undefined && (
                                <span className={`inline-block text-[10px] mt-1 px-1.5 py-0.5 rounded ${p.verified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {p.verified ? 'Verified' : 'Pending'}
                                </span>
                              )}
                            </td>
                            <td className="p-3 text-xs text-slate-gray align-top">
                              {p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}
                            </td>
                            <td className="p-3 align-top">
                              {Array.isArray(p.errors) ? (
                                <ul className="space-y-1">
                                  {p.errors.map((err, i) => (
                                    <li key={i} className="text-xs">
                                      <span className="font-mono font-semibold text-red-700">{err.field || '(root)'}</span>
                                      <span className="text-slate-gray"> — {err.msg}</span>
                                      {err.input && <span className="text-slate-400"> · got <span className="font-mono">{err.input}</span></span>}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-xs text-red-700 font-mono break-all">{p.error}</p>
                              )}
                            </td>
                            <td className="p-3 text-right align-top">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { if (window.confirm(`Delete provider record ${p.provider_id}?`)) handleDeleteInvalidProvider(p.provider_id); }}
                                className="rounded-lg text-xs text-red-600 hover:bg-red-50 border-red-200"
                              >
                                <Trash2 className="w-3.5 h-3.5 mr-1" />Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/50">
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Provider</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Category</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Status</th>
                        <th className="text-right p-4 text-sm font-semibold text-slate-gray">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {providers.map(p => (
                        <tr key={p.provider_id} className="border-b border-slate-50 hover:bg-slate-50/50">
                          <td className="p-4">
                            <p className="font-medium text-dark-slate text-sm">{p.name}</p>
                            <p className="text-xs text-slate-gray">{p.email || p.user_id}</p>
                          </td>
                          <td className="p-4">
                            <Badge variant="outline" className="text-xs">{p.category}</Badge>
                          </td>
                          <td className="p-4">
                            {p.verified ? (
                              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Verified</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Pending</Badge>
                            )}
                          </td>
                          <td className="p-4 text-right space-x-2">
                            <Button size="sm" variant="outline" onClick={() => openProviderDetail(p)} className="rounded-lg" data-testid={`view-provider-${p.provider_id}`}>
                              <Eye className="w-4 h-4 mr-1" /> View
                            </Button>
                            {!p.verified ? (
                              <Button size="sm" onClick={() => handleVerifyProvider(p.provider_id, true)} className="bg-emerald-600 text-white rounded-lg" data-testid={`verify-provider-${p.provider_id}`}>
                                <CheckCircle className="w-4 h-4 mr-1" /> Verify
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => handleVerifyProvider(p.provider_id, false)} className="rounded-lg" data-testid={`unverify-provider-${p.provider_id}`}>
                                <XCircle className="w-4 h-4 mr-1" /> Revoke
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {providers.length === 0 && (
                        <tr><td colSpan={4} className="p-8 text-center text-slate-gray">No providers found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* INVITES TAB */}
          <TabsContent value="invites">
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-slate-100 p-6">
                <h3 className="font-heading font-bold text-lg text-dark-slate mb-1">Send an invite</h3>
                <p className="text-sm text-slate-gray mb-4">Invitees get an email with a one-click signup link. Toggle the bypass if they're not an HBCU alum.</p>
                <form onSubmit={handleCreateInvite} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm">Email</Label>
                      <Input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="erik.klaver@blueskycollaborative.org"
                        className="mt-1 rounded-xl"
                        data-testid="invite-email"
                        required
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer text-sm pb-2">
                        <input
                          type="checkbox"
                          checked={inviteCanBypass}
                          onChange={(e) => setInviteCanBypass(e.target.checked)}
                          className="w-4 h-4 rounded"
                          data-testid="invite-bypass"
                        />
                        <span className="text-dark-slate">Allow non-HBCU signup (skip the HBCU requirement)</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Personal note (optional)</Label>
                    <Textarea
                      value={inviteNote}
                      onChange={(e) => setInviteNote(e.target.value)}
                      placeholder="Hey Erik — joining you to GradLink so we can collaborate..."
                      rows={2}
                      className="mt-1 rounded-xl"
                      data-testid="invite-note"
                    />
                  </div>
                  <Button type="submit" disabled={creatingInvite} className="bg-dark-slate text-white rounded-lg" data-testid="invite-submit">
                    {creatingInvite ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending</> : <><Send className="w-4 h-4 mr-2" />Send Invite</>}
                  </Button>
                </form>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-heading font-bold text-lg text-dark-slate">Recent invites</h3>
                  <span className="text-xs text-slate-gray">{invites.length} total</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/50">
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Email</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Sent</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Status</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Bypass HBCU</th>
                        <th className="text-right p-4 text-sm font-semibold text-slate-gray">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invites.map(inv => {
                        const status = inv.accepted_at ? 'accepted' : (inv.expires_at && new Date(inv.expires_at) < new Date() ? 'expired' : 'pending');
                        return (
                          <tr key={inv.invite_id} className="border-b border-slate-50 hover:bg-slate-50/50">
                            <td className="p-4">
                              <p className="font-medium text-dark-slate text-sm">{inv.email}</p>
                              {inv.note && <p className="text-xs text-slate-gray mt-0.5 line-clamp-1">{inv.note}</p>}
                            </td>
                            <td className="p-4 text-xs text-slate-gray">
                              {inv.created_at ? formatDistanceToNow(new Date(inv.created_at), { addSuffix: true }) : '—'}
                            </td>
                            <td className="p-4">
                              {status === 'accepted' && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Accepted</Badge>}
                              {status === 'pending' && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Pending</Badge>}
                              {status === 'expired' && <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">Expired</Badge>}
                            </td>
                            <td className="p-4">
                              {inv.can_bypass_hbcu ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-slate-300" />}
                            </td>
                            <td className="p-4 text-right">
                              {!inv.accepted_at && (
                                <Button size="sm" variant="outline" onClick={() => handleDeleteInvite(inv.invite_id)} className="rounded-lg text-xs text-red-600 hover:bg-red-50 border-red-200">
                                  <Trash2 className="w-3.5 h-3.5 mr-1" />Revoke
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {invites.length === 0 && (
                        <tr><td colSpan={5} className="p-8 text-center text-slate-gray">No invites sent yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* FOUNDING PROVIDER REFERRALS TAB */}
          <TabsContent value="fp-referrals">
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-slate-100 p-6">
                <h3 className="font-heading font-bold text-lg text-dark-slate mb-1">Refer a Founding Provider</h3>
                <p className="text-sm text-slate-gray mb-4">Send a prospect a personalized invitation to apply as a GradLink Founding Provider. The email is credited to the referrer and the prospect's application is linked back here.</p>
                <form onSubmit={handleCreateFpReferral} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm">Prospect name <span className="text-red-500">*</span></Label>
                      <Input
                        value={fpForm.prospect_name}
                        onChange={(e) => setFpForm(p => ({ ...p, prospect_name: e.target.value }))}
                        placeholder="Full name"
                        className="mt-1 rounded-xl"
                        data-testid="fp-prospect-name"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Prospect email <span className="text-red-500">*</span></Label>
                      <Input
                        type="email"
                        value={fpForm.prospect_email}
                        onChange={(e) => setFpForm(p => ({ ...p, prospect_email: e.target.value }))}
                        placeholder="name@example.com"
                        className="mt-1 rounded-xl"
                        data-testid="fp-prospect-email"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Business / firm</Label>
                      <Input
                        value={fpForm.prospect_business}
                        onChange={(e) => setFpForm(p => ({ ...p, prospect_business: e.target.value }))}
                        placeholder="Business or firm name"
                        className="mt-1 rounded-xl"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Category</Label>
                      <Input
                        value={fpForm.prospect_category}
                        onChange={(e) => setFpForm(p => ({ ...p, prospect_category: e.target.value }))}
                        placeholder="e.g. Real Estate Agent"
                        className="mt-1 rounded-xl"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-sm">Profile link (optional)</Label>
                      <Input
                        value={fpForm.prospect_link}
                        onChange={(e) => setFpForm(p => ({ ...p, prospect_link: e.target.value }))}
                        placeholder="Link to their website, LinkedIn, or firm bio"
                        className="mt-1 rounded-xl"
                      />
                    </div>
                  </div>

                  <div className="pt-2 border-t border-slate-100">
                    <p className="text-xs font-semibold text-slate-gray uppercase tracking-wide mb-3">Who's referring them</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">Referrer name <span className="text-red-500">*</span></Label>
                        <Input
                          value={fpForm.referrer_name}
                          onChange={(e) => setFpForm(p => ({ ...p, referrer_name: e.target.value }))}
                          placeholder="Your name (or whoever is referring)"
                          className="mt-1 rounded-xl"
                          data-testid="fp-referrer-name"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Referrer institution</Label>
                        <Input
                          value={fpForm.referrer_institution}
                          onChange={(e) => setFpForm(p => ({ ...p, referrer_institution: e.target.value }))}
                          placeholder="e.g. Howard University"
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-sm">Relationship (optional)</Label>
                        <Input
                          value={fpForm.referrer_relationship}
                          onChange={(e) => setFpForm(p => ({ ...p, referrer_relationship: e.target.value }))}
                          placeholder="e.g. former client, classmate, family friend"
                          className="mt-1 rounded-xl"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm">Personal note from the referrer</Label>
                    <Textarea
                      value={fpForm.note}
                      onChange={(e) => setFpForm(p => ({ ...p, note: e.target.value }))}
                      placeholder="Share why you're referring this person — how you know them, the kind of work they do, who they'd be a great fit for in the community."
                      rows={4}
                      className="mt-1 rounded-xl"
                      data-testid="fp-note"
                    />
                    <p className="text-xs text-slate-gray mt-1">Goes into the email as a quoted note from {fpForm.referrer_name || 'the referrer'}.</p>
                  </div>

                  <Button type="submit" disabled={creatingFpReferral} className="bg-dark-slate text-white rounded-lg" data-testid="fp-referral-submit">
                    {creatingFpReferral ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending</> : <><Send className="w-4 h-4 mr-2" />Send Referral</>}
                  </Button>
                </form>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-heading font-bold text-lg text-dark-slate">Recent referrals</h3>
                  <span className="text-xs text-slate-gray">{fpReferrals.length} total</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/50">
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Prospect</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Referrer</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Sent</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Status</th>
                        <th className="text-right p-4 text-sm font-semibold text-slate-gray">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fpReferrals.map(r => {
                        const expired = r.expires_at && new Date(r.expires_at) < new Date();
                        const status = r.applied_at ? 'applied' : (expired ? 'expired' : 'pending');
                        return (
                          <tr key={r.referral_id} className="border-b border-slate-50 hover:bg-slate-50/50">
                            <td className="p-4">
                              <p className="font-medium text-dark-slate text-sm">{r.prospect_name}</p>
                              <p className="text-xs text-slate-gray">{r.prospect_email}</p>
                              {r.prospect_business && <p className="text-xs text-slate-gray mt-0.5">{r.prospect_business}{r.prospect_category ? ` — ${r.prospect_category}` : ''}</p>}
                            </td>
                            <td className="p-4">
                              <p className="text-sm text-dark-slate">{r.referrer_name}</p>
                              {r.referrer_institution && <p className="text-xs text-slate-gray">{r.referrer_institution}</p>}
                            </td>
                            <td className="p-4 text-xs text-slate-gray">
                              {r.created_at ? formatDistanceToNow(new Date(r.created_at), { addSuffix: true }) : '—'}
                            </td>
                            <td className="p-4">
                              {status === 'applied' && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Applied</Badge>}
                              {status === 'pending' && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Sent</Badge>}
                              {status === 'expired' && <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">Expired</Badge>}
                            </td>
                            <td className="p-4 text-right space-x-2">
                              {!r.applied_at && (
                                <>
                                  <Button size="sm" variant="outline" onClick={() => handleResendFpReferral(r.referral_id)} className="rounded-lg text-xs">
                                    <Send className="w-3.5 h-3.5 mr-1" />Resend
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => handleDeleteFpReferral(r.referral_id)} className="rounded-lg text-xs text-red-600 hover:bg-red-50 border-red-200">
                                    <Trash2 className="w-3.5 h-3.5 mr-1" />Revoke
                                  </Button>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {fpReferrals.length === 0 && (
                        <tr><td colSpan={5} className="p-8 text-center text-slate-gray">No referrals sent yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* PARTNER INVITES TAB */}
          <TabsContent value="partner-invites">
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-slate-100 p-6">
                <h3 className="font-heading font-bold text-lg text-dark-slate mb-1">Invite a Partner</h3>
                <p className="text-sm text-slate-gray mb-4">Send a recruiter, sponsor, or alumni association a complimentary invite link. They skip Stripe entirely — no credit card required.</p>
                <form onSubmit={handleCreatePartnerInvite} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm">Email <span className="text-red-500">*</span></Label>
                      <Input
                        type="email"
                        value={piForm.email}
                        onChange={(e) => setPiForm(p => ({ ...p, email: e.target.value }))}
                        placeholder="partner@company.com"
                        className="mt-1 rounded-xl"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Contact name</Label>
                      <Input
                        value={piForm.contact_name}
                        onChange={(e) => setPiForm(p => ({ ...p, contact_name: e.target.value }))}
                        placeholder="First and last name"
                        className="mt-1 rounded-xl"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Role <span className="text-red-500">*</span></Label>
                      <Select
                        value={piForm.role}
                        onValueChange={(val) => setPiForm(p => ({ ...p, role: val, plan: '' }))}
                      >
                        <SelectTrigger className="mt-1 rounded-xl">
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="recruiter">Recruiter</SelectItem>
                          <SelectItem value="sponsor">Sponsor / Advertiser</SelectItem>
                          <SelectItem value="alumni_association">Alumni Association</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Plan</Label>
                      <Select
                        value={piForm.plan}
                        onValueChange={(val) => setPiForm(p => ({ ...p, plan: val }))}
                        disabled={!piForm.role}
                      >
                        <SelectTrigger className="mt-1 rounded-xl">
                          <SelectValue placeholder={piForm.role ? 'Select a plan (defaults to mid-tier)' : 'Select a role first'} />
                        </SelectTrigger>
                        <SelectContent>
                          {(PARTNER_INVITE_PLANS[piForm.role] || []).map(plan => (
                            <SelectItem key={plan.id} value={plan.id}>{plan.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Organization</Label>
                      <Input
                        value={piForm.organization}
                        onChange={(e) => setPiForm(p => ({ ...p, organization: e.target.value }))}
                        placeholder="Company or institution name"
                        className="mt-1 rounded-xl"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Comp months</Label>
                      <Input
                        type="number"
                        min={1}
                        max={24}
                        value={piForm.comp_months}
                        onChange={(e) => setPiForm(p => ({ ...p, comp_months: e.target.value }))}
                        className="mt-1 rounded-xl"
                      />
                      <p className="text-xs text-slate-gray mt-1">Default is 6 months free</p>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm">Personal note (optional)</Label>
                    <Textarea
                      value={piForm.note}
                      onChange={(e) => setPiForm(p => ({ ...p, note: e.target.value }))}
                      placeholder="Add a personal note that will appear in the invite email — e.g. why you're inviting them or what you're excited to offer."
                      rows={3}
                      className="mt-1 rounded-xl"
                    />
                  </div>

                  <Button type="submit" disabled={creatingPartnerInvite} className="bg-dark-slate text-white rounded-lg">
                    {creatingPartnerInvite
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending</>
                      : <><Send className="w-4 h-4 mr-2" />Send Invite</>}
                  </Button>
                </form>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-heading font-bold text-lg text-dark-slate">Sent invites</h3>
                  <span className="text-xs text-slate-gray">{partnerInvites.length} total</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/50">
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Partner</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Role</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Plan</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Comp</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Sent</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-gray">Status</th>
                        <th className="text-right p-4 text-sm font-semibold text-slate-gray">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partnerInvites.map(inv => {
                        const expired = inv.expires_at && new Date(inv.expires_at) < new Date();
                        const status = inv.accepted_at ? 'accepted' : (expired ? 'expired' : 'pending');
                        const roleLabel = { recruiter: 'Recruiter', sponsor: 'Sponsor', alumni_association: 'Alumni Assoc.' }[inv.role] || inv.role;
                        return (
                          <tr key={inv.invite_id} className="border-b border-slate-50 hover:bg-slate-50/50">
                            <td className="p-4">
                              <p className="font-medium text-dark-slate text-sm">{inv.contact_name || inv.email}</p>
                              {inv.contact_name && <p className="text-xs text-slate-gray">{inv.email}</p>}
                              {inv.organization && <p className="text-xs text-slate-gray mt-0.5">{inv.organization}</p>}
                            </td>
                            <td className="p-4 text-sm text-dark-slate">{roleLabel}</td>
                            <td className="p-4 text-xs text-slate-gray">{inv.plan_name || inv.plan || '—'}</td>
                            <td className="p-4 text-xs text-slate-gray">{inv.comp_months || 6} mo</td>
                            <td className="p-4 text-xs text-slate-gray">
                              {inv.created_at ? formatDistanceToNow(new Date(inv.created_at), { addSuffix: true }) : '—'}
                            </td>
                            <td className="p-4">
                              {status === 'accepted' && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Accepted</Badge>}
                              {status === 'pending' && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Sent</Badge>}
                              {status === 'expired' && <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">Expired</Badge>}
                            </td>
                            <td className="p-4 text-right space-x-2">
                              {!inv.accepted_at && (
                                <>
                                  <Button size="sm" variant="outline" onClick={() => handleResendPartnerInvite(inv.invite_id)} className="rounded-lg text-xs">
                                    <Send className="w-3.5 h-3.5 mr-1" />Resend
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => handleDeletePartnerInvite(inv.invite_id)} className="rounded-lg text-xs text-red-600 hover:bg-red-50 border-red-200">
                                    <Trash2 className="w-3.5 h-3.5 mr-1" />Revoke
                                  </Button>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {partnerInvites.length === 0 && (
                        <tr><td colSpan={7} className="p-8 text-center text-slate-gray">No partner invites sent yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* REPORTS TAB */}
          <TabsContent value="reports">
            <div className="space-y-4">
              <div className="flex gap-2">
                {['pending', 'resolved', 'all'].map(f => (
                  <Button
                    key={f}
                    variant={reportFilter === f ? 'default' : 'outline'}
                    size="sm"
                    className="rounded-lg capitalize"
                    onClick={() => { setReportFilter(f); fetchReports(f); }}
                    data-testid={`report-filter-${f}`}
                  >
                    {f} {f === 'pending' && reportsTotal > 0 ? `(${reportsTotal})` : ''}
                  </Button>
                ))}
              </div>

              <div className="space-y-3">
                {reports.map(r => (
                  <div key={r.report_id} className="bg-white rounded-2xl p-5 border border-slate-100">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Flag className="w-4 h-4 text-amber-500" />
                          <span className="font-medium text-dark-slate capitalize">{r.content_type} Report</span>
                          <Badge variant="outline" className="text-xs">{r.reason}</Badge>
                        </div>
                        {r.details && <p className="text-sm text-slate-gray mb-2">{r.details}</p>}
                        <p className="text-xs text-slate-gray">
                          Reported {r.created_at ? formatDistanceToNow(new Date(r.created_at), { addSuffix: true }) : ''} | ID: {r.content_id}
                        </p>
                      </div>
                      {r.status === 'pending' && (
                        <div className="flex gap-2 ml-4">
                          <Button size="sm" variant="outline" onClick={() => handleResolveReport(r.report_id, 'dismiss')} className="rounded-lg" data-testid={`dismiss-report-${r.report_id}`}>
                            Dismiss
                          </Button>
                          <Button size="sm" onClick={() => handleResolveReport(r.report_id, 'remove_content')} className="bg-red-600 text-white rounded-lg" data-testid={`remove-content-${r.report_id}`}>
                            <Trash2 className="w-4 h-4 mr-1" /> Remove
                          </Button>
                        </div>
                      )}
                      {r.status === 'resolved' && (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          Resolved: {r.resolution_action}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
                {reports.length === 0 && (
                  <div className="text-center py-12 text-slate-gray">
                    <Flag className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                    <p>No {reportFilter} reports</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* AUDIT LOGS TAB */}
          <TabsContent value="audit">
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/50">
                      <th className="text-left p-4 text-sm font-semibold text-slate-gray">Action</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-gray">Target</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-gray">Admin</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-gray">Details</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-gray">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map(log => (
                      <tr key={log.log_id} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="p-4">
                          <Badge variant="outline" className="text-xs capitalize">{log.action?.replace(/_/g, ' ')}</Badge>
                        </td>
                        <td className="p-4 text-sm text-slate-gray">{log.target_type}: {log.target_id?.slice(0, 12)}...</td>
                        <td className="p-4 text-sm text-slate-gray">{log.admin_id?.slice(0, 12)}...</td>
                        <td className="p-4 text-sm text-slate-gray">{JSON.stringify(log.details || {}).slice(0, 50)}</td>
                        <td className="p-4 text-sm text-slate-gray">
                          {log.created_at ? formatDistanceToNow(new Date(log.created_at), { addSuffix: true }) : '-'}
                        </td>
                      </tr>
                    ))}
                    {auditLogs.length === 0 && (
                      <tr><td colSpan={5} className="p-8 text-center text-slate-gray">No audit logs yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* CONTENT MANAGEMENT TAB */}
          <TabsContent value="content">
            <div className="space-y-6">
              <h2 className="font-heading font-bold text-2xl text-dark-slate">Content Management</h2>

              {/* Curated Content (RSS) */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading font-bold text-lg text-dark-slate flex items-center gap-2">
                      <Rss className="w-5 h-5 text-mint-fresh" /> Curated Content (RSS)
                    </h3>
                    <p className="text-sm text-slate-gray mt-1">
                      Fetch articles from HBCU news feeds. Articles appear in the main feed between user posts.
                    </p>
                  </div>
                  <Button
                    onClick={async () => {
                      try {
                        toast.loading('Fetching RSS feeds...', { id: 'rss' });
                        const res = await curatedAPI.fetchRSS();
                        toast.success(`Fetched ${res.data.new_count} new articles`, { id: 'rss' });
                      } catch (e) {
                        toast.error('Failed to fetch RSS feeds', { id: 'rss' });
                      }
                    }}
                    className="bg-mint-fresh text-white hover:bg-mint-fresh/90 rounded-full"
                  >
                    <Download className="w-4 h-4 mr-2" /> Fetch Articles
                  </Button>
                </div>
                <div className="text-xs text-slate-gray bg-slate-50 rounded-xl p-3">
                  <p className="font-semibold mb-1">Default sources:</p>
                  <ul className="space-y-1">
                    <li>HBCU Buzz - hbcubuzz.com/feed/</li>
                    <li>HBCU Gameday - hbcugameday.com/feed/</li>
                    <li>Diverse Education - diverseeducation.com/feed</li>
                  </ul>
                  <p className="mt-2 text-slate-gray/70">Sources can be customized via the API.</p>
                </div>
              </div>

              {/* External Jobs */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading font-bold text-lg text-dark-slate flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-blue-500" /> External Jobs
                    </h3>
                    <p className="text-sm text-slate-gray mt-1">
                      Fetch jobs from RemoteOK and The Muse. Jobs appear in the External Jobs tab on the Jobs page.
                    </p>
                  </div>
                  <Button
                    onClick={async () => {
                      try {
                        toast.loading('Fetching external jobs...', { id: 'jobs' });
                        const res = await externalJobsAPI.fetch({ refresh: true });
                        toast.success(`Fetched ${res.data.new_count} new jobs`, { id: 'jobs' });
                      } catch (e) {
                        toast.error('Failed to fetch external jobs', { id: 'jobs' });
                      }
                    }}
                    className="bg-blue-600 text-white hover:bg-blue-700 rounded-full"
                  >
                    <Download className="w-4 h-4 mr-2" /> Fetch Jobs
                  </Button>
                </div>
                <div className="text-xs text-slate-gray bg-slate-50 rounded-xl p-3">
                  <p className="font-semibold mb-1">Sources:</p>
                  <ul className="space-y-1">
                    <li>RemoteOK - Remote tech jobs (30 per fetch)</li>
                    <li>The Muse - Engineering, Marketing, Finance jobs (entry/mid level)</li>
                  </ul>
                  <p className="mt-2 text-slate-gray/70">Deduplicates automatically. Fetch as often as needed.</p>
                </div>
              </div>

              {/* TikTok Content Curation */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <div className="flex items-center gap-2 mb-4">
                  <Video className="w-5 h-5 text-pink-500" />
                  <h3 className="font-heading font-bold text-lg text-dark-slate">TikTok Content Curation</h3>
                </div>
                <p className="text-sm text-slate-gray mb-4">
                  Paste TikTok video URLs and AI will score them against your content themes. Relevant videos get embedded in the feed.
                </p>

                {/* Quick single paste */}
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="Paste a TikTok video URL..."
                    value={tiktokUrl}
                    onChange={(e) => setTiktokUrl(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    onClick={async () => {
                      if (!tiktokUrl.trim()) return;
                      try {
                        toast.loading('AI is scoring this video...', { id: 'tiktok-single' });
                        const res = await curatedAPI.addTikTokSingle({ url: tiktokUrl });
                        if (res.data.status === 'duplicate') toast.info('Already in feed', { id: 'tiktok-single' });
                        else toast.success('TikTok video added to feed!', { id: 'tiktok-single' });
                        setTiktokUrl('');
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Failed to add video', { id: 'tiktok-single' });
                      }
                    }}
                    className="bg-pink-500 text-white hover:bg-pink-600 rounded-full"
                    disabled={!tiktokUrl.trim()}
                  >
                    Add
                  </Button>
                </div>

                {/* Bulk paste */}
                <Textarea
                  placeholder="Or paste multiple TikTok URLs (one per line) for bulk AI curation..."
                  value={tiktokBulkUrls}
                  onChange={(e) => setTiktokBulkUrls(e.target.value)}
                  rows={3}
                  className="mb-2"
                />
                <Button
                  onClick={async () => {
                    const urls = tiktokBulkUrls.split('\n').map(u => u.trim()).filter(u => u);
                    if (urls.length === 0) return;
                    try {
                      toast.loading(`AI is scoring ${urls.length} videos...`, { id: 'tiktok-bulk' });
                      const res = await curatedAPI.fetchTikTok({ urls });
                      toast.success(res.data.message, { id: 'tiktok-bulk' });
                      setTiktokBulkUrls('');
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to process videos', { id: 'tiktok-bulk' });
                    }
                  }}
                  variant="outline"
                  className="rounded-full"
                  disabled={!tiktokBulkUrls.trim()}
                >
                  <Download className="w-4 h-4 mr-2" /> Fetch & Curate ({tiktokBulkUrls.split('\n').filter(u => u.trim()).length} URLs)
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      toast.loading('Migrating existing embeds...', { id: 'migrate' });
                      const res = await curatedAPI.migrateTikTokEmbeds();
                      toast.success(res.data.message, { id: 'migrate' });
                    } catch (e) {
                      toast.error('Migration failed', { id: 'migrate' });
                    }
                  }}
                  variant="ghost"
                  size="sm"
                  className="ml-2 text-xs text-slate-gray"
                >
                  Migrate embeds
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      toast.loading('Pulling latest YouTube videos from creators…', { id: 'refresh-yt' });
                      const res = await curatedAPI.refreshYouTubeCreators(3);
                      const d = res.data || {};
                      toast.success(`Added ${d.videos_added || 0} YouTube videos across ${d.creators_processed || 0} creator(s)`, { id: 'refresh-yt' });
                      const issues = [];
                      if ((d.could_not_resolve || []).length) issues.push(`Could not resolve handle: ${d.could_not_resolve.join(', ')}`);
                      if ((d.no_videos_found || []).length) issues.push(`No new videos: ${d.no_videos_found.join(', ')}`);
                      if (issues.length) toast.message(issues.join(' · '), { duration: 10000 });
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'YouTube refresh failed', { id: 'refresh-yt' });
                    }
                  }}
                  variant="outline"
                  size="sm"
                  className="ml-2 text-xs rounded-full bg-red-50 border-red-200 hover:bg-red-100"
                  title="Pull latest YouTube videos from every active YouTube creator and add them to the feed"
                >
                  Refresh YouTube creators
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      toast.loading('Removing duplicates...', { id: 'dedupe' });
                      const res = await curatedAPI.dedupe();
                      toast.success(res.data.message, { id: 'dedupe' });
                    } catch (e) {
                      toast.error('Dedupe failed', { id: 'dedupe' });
                    }
                  }}
                  variant="ghost"
                  size="sm"
                  className="ml-2 text-xs text-slate-gray"
                >
                  Remove duplicates
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      toast.loading('Fetching article images...', { id: 'backfill' });
                      const res = await curatedAPI.backfillImages();
                      toast.success(res.data.message, { id: 'backfill' });
                    } catch (e) {
                      toast.error('Backfill failed', { id: 'backfill' });
                    }
                  }}
                  variant="ghost"
                  size="sm"
                  className="ml-2 text-xs text-slate-gray"
                >
                  Backfill images
                </Button>
              </div>

              {/* Content Themes */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <h3 className="font-heading font-bold text-lg text-dark-slate mb-3">Content Themes</h3>
                <p className="text-sm text-slate-gray mb-4">AI uses these themes to score TikTok content relevance.</p>
                <div className="space-y-2 mb-4">
                  {themes.map(t => (
                    <div key={t.theme_id} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-dark-slate">{t.name}</p>
                        <p className="text-xs text-slate-gray truncate">{t.description}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        <button
                          onClick={async () => {
                            await curatedAPI.manageTheme({ action: 'toggle', theme_id: t.theme_id, active: !t.active });
                            setThemes(prev => prev.map(th => th.theme_id === t.theme_id ? { ...th, active: !th.active } : th));
                          }}
                          className={`text-xs px-2 py-1 rounded-full ${t.active ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}
                        >
                          {t.active ? 'Active' : 'Off'}
                        </button>
                        <button
                          onClick={async () => {
                            await curatedAPI.manageTheme({ action: 'remove', theme_id: t.theme_id });
                            setThemes(prev => prev.filter(th => th.theme_id !== t.theme_id));
                          }}
                          className="text-slate-gray hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {themes.length === 0 && <p className="text-sm text-slate-gray">Loading themes...</p>}
                </div>
                <div className="flex gap-2">
                  <Input placeholder="Theme name" value={newThemeName} onChange={(e) => setNewThemeName(e.target.value)} className="w-1/3" />
                  <Input placeholder="Description" value={newThemeDesc} onChange={(e) => setNewThemeDesc(e.target.value)} className="flex-1" />
                  <Button
                    onClick={async () => {
                      if (!newThemeName.trim()) return;
                      const res = await curatedAPI.manageTheme({ action: 'add', name: newThemeName, description: newThemeDesc });
                      setNewThemeName(''); setNewThemeDesc('');
                      const updated = await curatedAPI.getThemes();
                      setThemes(updated.data);
                    }}
                    variant="outline"
                    className="rounded-full"
                    disabled={!newThemeName.trim()}
                  >
                    Add
                  </Button>
                </div>
              </div>

              {/* TikTok Creators */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <h3 className="font-heading font-bold text-lg text-dark-slate mb-3">TikTok Creators</h3>
                <p className="text-sm text-slate-gray mb-4">Reference list of HBCU creators to check for new content.</p>
                <div className="space-y-2 mb-4">
                  {creators.map(c => (
                    <div key={c.creator_id} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                      <div>
                        <p className="font-semibold text-sm text-dark-slate">{c.display_name || c.username}</p>
                        <p className="text-xs text-slate-gray">{c.profile_url}</p>
                      </div>
                      <button
                        onClick={async () => {
                          await curatedAPI.manageCreator({ action: 'remove', creator_id: c.creator_id });
                          setCreators(prev => prev.filter(cr => cr.creator_id !== c.creator_id));
                        }}
                        className="text-slate-gray hover:text-red-500 ml-3"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {creators.length === 0 && <p className="text-sm text-slate-gray">No creators added yet.</p>}
                </div>
                <div className="flex gap-2">
                  <Input placeholder="@username" value={newCreatorUser} onChange={(e) => setNewCreatorUser(e.target.value)} className="w-1/3" />
                  <Input placeholder="https://tiktok.com/@..." value={newCreatorUrl} onChange={(e) => setNewCreatorUrl(e.target.value)} className="flex-1" />
                  <Button
                    onClick={async () => {
                      if (!newCreatorUrl.trim()) return;
                      await curatedAPI.manageCreator({ action: 'add', username: newCreatorUser, profile_url: newCreatorUrl, display_name: newCreatorUser });
                      setNewCreatorUser(''); setNewCreatorUrl('');
                      const updated = await curatedAPI.getCreators();
                      setCreators(updated.data);
                    }}
                    variant="outline"
                    className="rounded-full"
                    disabled={!newCreatorUrl.trim()}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Roles Dialog */}
        <Dialog open={!!rolesDialog} onOpenChange={() => setRolesDialog(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Manage User Roles</DialogTitle>
            </DialogHeader>
            {rolesDialog && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={rolesDialog.profile_photo} />
                    <AvatarFallback className="bg-mint-fresh text-dark-slate">{rolesDialog.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{rolesDialog.name}</p>
                    <p className="text-sm text-slate-gray">{rolesDialog.email}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {ALL_ROLES.map(role => (
                    <label key={role} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:bg-slate-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingRoles.includes(role)}
                        onChange={() => toggleRole(role)}
                        className="w-4 h-4 rounded border-slate-300 text-dark-slate focus:ring-dark-slate"
                      />
                      <div>
                        <p className="font-medium text-sm text-dark-slate capitalize">{role.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-slate-gray">
                          {role === 'admin' && 'Full platform access'}
                          {role === 'moderator' && 'Content moderation & reports'}
                          {role === 'alumni_association' && 'Alumni Association panel access'}
                          {role === 'recruiter' && 'Recruiter panel & talent search'}
                          {role === 'sponsor' && 'Sponsor panel & ad campaigns'}
                          {role === 'advertiser' && 'Ad campaign management'}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setRolesDialog(null)}>Cancel</Button>
              <Button onClick={handleUpdateRoles} className="bg-dark-slate text-white">
                <Save className="w-4 h-4 mr-1" /> Save Roles
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* User Status Dialog */}
        <Dialog open={!!statusDialog} onOpenChange={() => setStatusDialog(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Update User Status</DialogTitle>
            </DialogHeader>
            {statusDialog && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-mint-fresh text-dark-slate">{statusDialog.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{statusDialog.name}</p>
                    <p className="text-sm text-slate-gray">{statusDialog.email}</p>
                  </div>
                </div>
                <Select value={statusAction} onValueChange={setStatusAction}>
                  <SelectTrigger data-testid="status-action-select">
                    <SelectValue placeholder="Select action..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Activate</SelectItem>
                    <SelectItem value="suspended">Suspend</SelectItem>
                    <SelectItem value="banned">Ban</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea
                  placeholder="Reason (optional)..."
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  data-testid="status-reason-input"
                />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setStatusDialog(null)}>Cancel</Button>
              <Button onClick={handleUserStatusChange} disabled={!statusAction} className="bg-dark-slate text-white" data-testid="confirm-status-change">
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* User Detail Dialog */}
        <Dialog open={!!userDetailDialog} onOpenChange={() => setUserDetailDialog(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>User Details</DialogTitle>
            </DialogHeader>
            {userDetailDialog && (
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <Avatar className="w-14 h-14">
                    <AvatarImage src={userDetailDialog.profile_photo} />
                    <AvatarFallback className="bg-mint-fresh text-dark-slate text-lg">{userDetailDialog.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-heading font-bold text-lg text-dark-slate">{userDetailDialog.name}</p>
                    <p className="text-sm text-slate-gray">{userDetailDialog.email}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-slate-gray text-xs font-medium mb-0.5">Institution</p>
                    <p className="text-dark-slate">{userDetailDialog.institution || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-gray text-xs font-medium mb-0.5">Graduation Year</p>
                    <p className="text-dark-slate">{userDetailDialog.graduation_year || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-gray text-xs font-medium mb-0.5">Major</p>
                    <p className="text-dark-slate">{userDetailDialog.major || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-gray text-xs font-medium mb-0.5">Title</p>
                    <p className="text-dark-slate">{userDetailDialog.title || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-gray text-xs font-medium mb-0.5">Company</p>
                    <p className="text-dark-slate">{userDetailDialog.company || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-gray text-xs font-medium mb-0.5">Location</p>
                    <p className="text-dark-slate">{userDetailDialog.location || '-'}</p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <div>
                    <p className="text-slate-gray text-xs font-medium mb-1">Roles</p>
                    <div className="flex flex-wrap gap-1">
                      {(userDetailDialog.roles || []).length > 0 ? (userDetailDialog.roles || []).map(r => (
                        <Badge key={r} variant="outline" className="text-xs capitalize bg-blue-50 text-blue-700 border-blue-200">{r.replace(/_/g, ' ')}</Badge>
                      )) : (
                        <span className="text-xs text-slate-gray">No special roles</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <div>
                    <p className="text-slate-gray text-xs font-medium mb-0.5">Status</p>
                    {statusBadge(userDetailDialog.status)}
                  </div>
                  <div>
                    <p className="text-slate-gray text-xs font-medium mb-0.5">Profile Completion</p>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${getProfileCompletion(userDetailDialog)}%` }} />
                      </div>
                      <span className="text-xs text-dark-slate font-medium">{getProfileCompletion(userDetailDialog)}%</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-slate-gray text-xs font-medium mb-0.5">Joined</p>
                    <p className="text-dark-slate text-sm">
                      {userDetailDialog.created_at ? formatDistanceToNow(new Date(userDetailDialog.created_at), { addSuffix: true }) : '-'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
              <div className="flex gap-2 flex-1">
                {userDetailDialog && userDetailDialog.role !== 'admin' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                    onClick={() => setDeleteConfirm(userDetailDialog)}
                    data-testid="user-detail-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Delete User
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                {userDetailDialog && userDetailDialog.role !== 'admin' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setUserDetailDialog(null); setStatusDialog(userDetailDialog); }}
                  >
                    <Ban className="w-4 h-4 mr-1" /> Change Status
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { const u = userDetailDialog; setUserDetailDialog(null); openRolesDialog(u); setActiveTab('roles'); }}
                >
                  <UserCog className="w-4 h-4 mr-1" /> Edit Roles
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Provider Detail Dialog */}
        <Dialog open={!!providerDialog} onOpenChange={() => setProviderDialog(null)}>
          <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-dark-slate">Provider Details</DialogTitle>
            </DialogHeader>
            {providerDialog && (
              <div className="space-y-5 mt-2">
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center gap-2 flex-shrink-0">
                    <label className="relative w-20 h-20 rounded-2xl border border-slate-200 overflow-hidden cursor-pointer group" data-testid="provider-photo-upload">
                      {providerDialog.profile_photo ? (
                        <img src={providerDialog.profile_photo} alt={providerDialog.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-slate-100 flex items-center justify-center text-slate-400 text-2xl font-semibold">
                          {(providerDialog.name || '?').charAt(0)}
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center text-white opacity-0 group-hover:opacity-100">
                        {uploadingProviderPhoto ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploadingProviderPhoto}
                        onChange={(e) => handleProviderPhotoUpload(e.target.files?.[0])}
                      />
                    </label>
                    <label className="text-xs text-dark-slate font-medium cursor-pointer hover:underline inline-flex items-center gap-1" data-testid="provider-photo-upload-button">
                      {uploadingProviderPhoto ? <><Loader2 className="w-3 h-3 animate-spin" />Uploading…</> : <><Camera className="w-3 h-3" />Change photo</>}
                      <input type="file" accept="image/*" className="hidden" disabled={uploadingProviderPhoto} onChange={(e) => handleProviderPhotoUpload(e.target.files?.[0])} />
                    </label>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-xl text-dark-slate">{providerDialog.name || '—'}</p>
                    {providerDialog.business_name && <p className="text-sm text-slate-gray">{providerDialog.business_name}</p>}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge variant="outline" className="text-xs capitalize">{(providerDialog.category || '').replace(/_/g, ' ')}</Badge>
                      {providerDialog.verified ? (
                        <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">Verified</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">Pending</Badge>
                      )}
                    </div>
                  </div>
                </div>

                {providerDialog.business_description && (
                  <div>
                    <p className="text-xs font-semibold text-slate-gray uppercase tracking-wide mb-1">About</p>
                    <p className="text-sm text-dark-slate whitespace-pre-wrap">{providerDialog.business_description}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs font-semibold text-slate-gray uppercase tracking-wide mb-1">Email</p>
                    <p className="text-dark-slate break-all">{providerDialog.contact_email || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-gray uppercase tracking-wide mb-1">Phone</p>
                    <p className="text-dark-slate">{providerDialog.contact_phone || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-gray uppercase tracking-wide mb-1">Location</p>
                    <p className="text-dark-slate">{providerDialog.location || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-gray uppercase tracking-wide mb-1">Website</p>
                    {providerDialog.website_url ? (
                      <a href={extUrl(providerDialog.website_url)} target="_blank" rel="noopener noreferrer" className="text-dark-slate underline break-all">{providerDialog.website_url}</a>
                    ) : <p className="text-slate-gray">—</p>}
                  </div>
                </div>

                {(providerDialog.instagram_url || providerDialog.facebook_url || providerDialog.tiktok_url || providerDialog.linkedin_url) && (
                  <div>
                    <p className="text-xs font-semibold text-slate-gray uppercase tracking-wide mb-1">Social</p>
                    <div className="space-y-1 text-sm">
                      {providerDialog.instagram_url && <p><span className="text-slate-gray">Instagram:</span> <a href={extUrl(providerDialog.instagram_url)} target="_blank" rel="noopener noreferrer" className="text-dark-slate underline break-all">{providerDialog.instagram_url}</a></p>}
                      {providerDialog.facebook_url && <p><span className="text-slate-gray">Facebook:</span> <a href={extUrl(providerDialog.facebook_url)} target="_blank" rel="noopener noreferrer" className="text-dark-slate underline break-all">{providerDialog.facebook_url}</a></p>}
                      {providerDialog.tiktok_url && <p><span className="text-slate-gray">TikTok:</span> <a href={extUrl(providerDialog.tiktok_url)} target="_blank" rel="noopener noreferrer" className="text-dark-slate underline break-all">{providerDialog.tiktok_url}</a></p>}
                      {providerDialog.linkedin_url && <p><span className="text-slate-gray">LinkedIn:</span> <a href={extUrl(providerDialog.linkedin_url)} target="_blank" rel="noopener noreferrer" className="text-dark-slate underline break-all">{providerDialog.linkedin_url}</a></p>}
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-xs font-semibold text-slate-gray uppercase tracking-wide">HBCU Affiliations &amp; Credentials</Label>
                  <Textarea
                    value={providerCredentialsEdit}
                    onChange={(e) => setProviderCredentialsEdit(e.target.value)}
                    placeholder="HBCU Alum: Southern University of New Orleans, GradLink Founding Provider"
                    className="mt-1 rounded-xl text-sm"
                    rows={2}
                    data-testid="provider-credentials-edit"
                  />
                  <p className="text-xs text-slate-gray mt-1">Comma-separated. For HBCU affiliations, prefix with <span className="font-mono">HBCU Alum:</span> so the school logo appears. Spell out school names in full.</p>
                  <Button
                    size="sm"
                    onClick={handleSaveProviderCredentials}
                    disabled={savingProvider || providerCredentialsEdit === (providerDialog.credentials || []).join(', ')}
                    className="mt-2 bg-dark-slate text-white rounded-lg"
                    data-testid="save-provider-credentials"
                  >
                    {savingProvider ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving</> : <><Save className="w-4 h-4 mr-1" /> Save Credentials</>}
                  </Button>
                </div>

                <div className="text-xs text-slate-gray">
                  <span className="font-semibold">Provider ID:</span> <span className="font-mono">{providerDialog.provider_id}</span>
                </div>
              </div>
            )}
            <DialogFooter className="flex-row gap-2 justify-end">
              <Button variant="outline" onClick={() => setProviderDialog(null)}>Close</Button>
              {providerDialog && (
                providerDialog.verified ? (
                  <Button variant="outline" onClick={() => handleVerifyProvider(providerDialog.provider_id, false)}>
                    <XCircle className="w-4 h-4 mr-1" /> Revoke Verification
                  </Button>
                ) : (
                  <Button onClick={() => handleVerifyProvider(providerDialog.provider_id, true)} className="bg-emerald-600 text-white hover:bg-emerald-700">
                    <CheckCircle className="w-4 h-4 mr-1" /> Verify Provider
                  </Button>
                )
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete User Confirmation Dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete User</DialogTitle>
            </DialogHeader>
            {deleteConfirm && (
              <div className="space-y-3">
                <p className="text-sm text-slate-gray">
                  Are you sure you want to delete <span className="font-semibold text-dark-slate">{deleteConfirm.name}</span>? This cannot be undone.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
              <Button
                onClick={() => deleteConfirm && handleDeleteUser(deleteConfirm.user_id)}
                className="bg-red-600 text-white hover:bg-red-700"
                data-testid="confirm-delete-user"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, color, onClick }) => {
  const interactive = typeof onClick === 'function';
  const Tag = interactive ? 'button' : 'div';
  return (
    <Tag
      type={interactive ? 'button' : undefined}
      onClick={onClick}
      className={`text-left bg-white rounded-2xl p-5 border border-slate-100 ${interactive ? 'hover:border-slate-300 hover:shadow-md transition-all cursor-pointer w-full' : ''}`}
    >
      <div className={`w-10 h-10 rounded-xl bg-${color}-100 flex items-center justify-center mb-3`}>
        <Icon className={`w-5 h-5 text-${color}-600`} />
      </div>
      <p className="font-heading font-bold text-2xl text-dark-slate">{value}</p>
      <p className="text-sm text-slate-gray">{label}</p>
      {sub && <p className="text-xs text-slate-gray mt-1">{sub}</p>}
    </Tag>
  );
};

const ContentCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-xl p-4 border border-slate-100 flex items-center gap-3">
    <Icon className="w-5 h-5 text-slate-gray" />
    <div>
      <p className="font-bold text-dark-slate">{value}</p>
      <p className="text-xs text-slate-gray">{label}</p>
    </div>
  </div>
);

export default AdminPanel;
